import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '../utils/dateTime';
import { RootState } from '../store';
import { eventsService } from '@cobu/api-client';
import type { Gathering, RSVP, GroupMember, Comment, UserInfo } from '@cobu/types';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';
import { showErrorMessage, showSuccessMessage } from '../utils/messageToast';
import { Strings } from '../constants/Strings';
import {
  getParentEventDetails,
  updateParentRsvp,
  type ParentEventId,
} from '../lib/integrations/homebodyRxpRsvp';
import { getEventFee } from '../lib/events/rateDetails';

const PARTNER_EVENT_BYPASS_BUILDING_UUID = '5059cc4e-cc7a-4f13-a1d4-f5086ea5367b';

interface UseEventDetailReturn {
  // Data
  gathering: Gathering | null;
  rsvps: RSVP[];
  currentMembers: GroupMember[];
  currentUser: UserInfo | null;
  eventDate: string;

  // States
  isLoading: boolean;
  error: string | null;
  userIsGroupMember: boolean;
  isCurrentUserCreator: boolean;
  rsvpObj: RSVP | null;
  isAllMembersModalVisible: boolean;
  isReportModalVisible: boolean;
  isRsvpActionPending: boolean;
  isParentDetailsLoading: boolean;

  // Computed values
  totalAttendance: number;
  rsvpTotalCapacity?: number | null;

  // Handlers
  handleRSVPPress: (totalAttendees?: number) => Promise<void>;
  handleUpdateRSVP: (totalAttendees: number) => Promise<void>;
  handleCancelRSVP: () => Promise<void>;
  handleCommentSubmit: (values: any, flag?: any, hide?: any, sessionReport?: any, callback?: () => void) => Promise<any>;
  handleDeleteComment: (comment: Comment) => Promise<void>;
  handleCancelGathering: () => Promise<void>;
  handleWhosComingPress: () => void;
  handleCloseAllMembersModal: () => void;
  handleReportPress: () => void;
  handleCloseReportModal: () => void;
  handleBlockUserPress: () => Promise<void>;
  handleClose: () => void;
  handleRetry: () => void;
  refreshData: { refreshing: boolean; onRefresh: () => Promise<void> };
  handleComposeComment: (replyTo?: any) => void;
}

interface UseEventDetailProps {
  eventId: string;
  currentEventDate: string;
}

export const useEventDetail = ({ eventId, currentEventDate }: UseEventDetailProps): UseEventDetailReturn => {
  const user = useSelector((state: RootState) => state.auth.user) as UserInfo | null;
  const navigate = useNavigate();
  const strings = Strings.get();
  const queryClient = useQueryClient();

  // Local loading states
  const [isAllMembersModalVisible, setIsAllMembersModalVisible] = useState(false);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [isParentRsvpPending, setIsParentRsvpPending] = useState(false);
  const isSyncingRsvpRef = useRef(false);

  // Fetch gathering
  // Try with eventDate first (for recurring events to match timing), but retry without it if we get 400
  const gatheringQuery = useQuery({
    queryKey: ['gathering', eventId, currentEventDate],
    queryFn: async () => {
      try {
        // Try with eventDate if provided (ensures timing matches list page)
        if (currentEventDate && currentEventDate.trim() !== '') {
          return await eventsService.getGathering(eventId, currentEventDate.trim());
        }
        return await eventsService.getGathering(eventId);
      } catch (error: any) {
        // If we get a 400 error with eventDate, retry without it (non-recurring events)
        if (error?.response?.status === 400 && currentEventDate && currentEventDate.trim() !== '') {
          return await eventsService.getGathering(eventId);
        }
        throw error;
      }
    },
    enabled: !!eventId,
  });
  const gathering = (gatheringQuery.data as Gathering | undefined) || null;

  const eventDate = useMemo(() => {
    if (!gathering) return '';
    return formatDateTime(
      gathering.scheduledStartTime,
      gathering.buildingTimezone || user?.building?.timezone || 'America/New_York',
      'YYYY-MM-DD'
    );
  }, [gathering, (user as any)?.building?.timezone]);

  const parentEventId = useMemo<ParentEventId | null>(() => {
    if (!gathering?.externalEvent) return null;
    const numeric = Number(gathering.externalEvent);
    return Number.isFinite(numeric) ? numeric : gathering.externalEvent;
  }, [gathering?.externalEvent]);

  const isBypassMode = user?.building?.uuid === PARTNER_EVENT_BYPASS_BUILDING_UUID;

  // Fee charged by the parent app when the resident RSVPs; null for free events.
  const eventFee = useMemo(() => getEventFee(gathering?.rateDetails), [gathering?.rateDetails]);

  const parentEventDetailsQuery = useQuery({
    queryKey: ['homebodyRxpEventDetails', parentEventId],
    queryFn: () => getParentEventDetails(parentEventId!),
    enabled: !!parentEventId && !isBypassMode,
  });

  // RSVPs
  const rsvpsQuery = useQuery({
    queryKey: ['gatheringRSVPs', eventId, eventDate],
    queryFn: () => eventsService.getGatheringRSVPs(eventId, eventDate),
    enabled: !!gathering && !!eventDate,
  });

  // Group members (if group)
  const groupId = gathering?.groupId;
  const groupMembersQuery = useQuery({
    queryKey: ['groupMembers', groupId],
    queryFn: () => eventsService.getGroupMembers(groupId || ''),
    enabled: !!groupId,
  });

  // Mutations
  const createRSVPMutation = useMutation({
    mutationFn: ({ totalAttendees }: { totalAttendees: number }) =>
      eventsService.createGatheringRSVP(eventId, user?.uuid || '', totalAttendees, eventDate),
  });
  const updateRSVPMutation = useMutation({
    mutationFn: ({ rsvpId, totalAttendees }: { rsvpId: string; totalAttendees: number }) =>
      eventsService.updateGatheringRSVP(rsvpId, totalAttendees, eventDate),
  });
  const deleteRSVPMutation = useMutation({
    mutationFn: ({ rsvpId }: { rsvpId: string }) => eventsService.deleteGatheringRSVP(rsvpId),
  });
  const cancelGatheringMutation = useMutation({
    mutationFn: () => eventsService.cancelGathering(eventId),
  });

  // Extract data from queries
  const parentEventDetails = parentEventDetailsQuery.data || null;
  const rsvps = (rsvpsQuery.data as RSVP[] | undefined) || [];
  const currentMembers = (groupMembersQuery.data as any[] | undefined) || [] as GroupMember[];

  // Computed states
  const userIsGroupMember = useMemo(() => {
    if (!groupId) return true; // For building-wide events, assume user is a member
    if (!user) return false;
    return currentMembers.some(member => member.user.uuid === user.uuid);
  }, [groupId, currentMembers, user]);

  const rsvpObj = useMemo(() => {
    if (!user) return null;
    if (gathering?.isPartnerEvent && !isBypassMode) {
      // Partner events: parent postMessage is the sole source of truth for RSVP state
      if (!parentEventDetails?.isAttending) return null;
      return {
        uuid: `homebody-rxp-${parentEventDetails.id}`,
        user: {
          uuid: user.uuid,
          name: user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' '),
          displayName: user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' '),
          profileImage: user.profileImage,
        },
        totalAttendees: 1,
        createdAt: gathering.createdAt,
      } as RSVP;
    }
    return rsvps.find(rsvp => rsvp.user.uuid === user.uuid) ?? null;
  }, [gathering?.isPartnerEvent, gathering?.createdAt, rsvps, parentEventDetails, user, isBypassMode]);

  // Check if current user is the creator of the event
  const isCurrentUserCreator = useMemo(() => {
    return user?.uuid === gathering?.creator?.uuid;
  }, [user?.uuid, gathering?.creator?.uuid]);

  // Calculate total attendance from RSVPs
  const totalAttendance = useMemo(() => {
    if (parentEventDetails?.numberAttending != null) return parentEventDetails.numberAttending;
    return rsvps.reduce((total, rsvp) => total + rsvp.totalAttendees, 0);
  }, [parentEventDetails, rsvps]);

  // Overall loading state
  const isLoading = gatheringQuery.isLoading || rsvpsQuery.isLoading || groupMembersQuery.isLoading;

  // Error handling
  const error = (gatheringQuery.error as any)?.message || (rsvpsQuery.error as any)?.message || (groupMembersQuery.error as any)?.message || null;

  // Reconcile Cobu RSVP state with parent (source of truth) after both queries settle
  useEffect(() => {
    if (isSyncingRsvpRef.current) return;
    if (!parentEventId || !user || !parentEventDetails || !eventDate) return;
    if (parentEventDetailsQuery.isFetching || rsvpsQuery.isFetching) return;
    if (isParentRsvpPending) return;

    const cobuRsvp = rsvps.find(r => r.user.uuid === user.uuid);

    if (parentEventDetails.isAttending && !cobuRsvp) {
      isSyncingRsvpRef.current = true;
      createRSVPMutation.mutateAsync({ totalAttendees: 1 })
        .then(() => rsvpsQuery.refetch())
        .catch(err => logger.error('RSVP state sync failed (create):', err))
        .finally(() => { isSyncingRsvpRef.current = false; });
    } else if (!parentEventDetails.isAttending && cobuRsvp) {
      isSyncingRsvpRef.current = true;
      deleteRSVPMutation.mutateAsync({ rsvpId: cobuRsvp.uuid })
        .then(() => rsvpsQuery.refetch())
        .catch(err => logger.error('RSVP state sync failed (delete):', err))
        .finally(() => { isSyncingRsvpRef.current = false; });
    }
  }, [
    parentEventId,
    user,
    parentEventDetails?.isAttending,
    parentEventDetailsQuery.isFetching,
    rsvpsQuery.isFetching,
    rsvps,
    eventDate,
    isParentRsvpPending,
  ]);

  // Track analytics when gathering loads
  useEffect(() => {
    if (gathering && user?.building?.name) {
      /* ANALYTICS_DISABLED: VIEW_EVENT_DETAIL ('view_event_detail')
        analyticsService.trackEvent(AnalyticsEvent.VIEW_EVENT_DETAIL, {
                eventId,
                eventTitle: gathering.title,
                buildingName: user.building.name,
              });
        */
    }
  }, [gathering, user?.building?.name, eventId]);
  // Navigation handlers
  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    gatheringQuery.refetch();
    parentEventDetailsQuery.refetch();
    if (eventDate) rsvpsQuery.refetch();
    if (groupId) groupMembersQuery.refetch();
  }, [gatheringQuery, parentEventDetailsQuery, rsvpsQuery, groupMembersQuery, eventDate, groupId]);

  const applyParentRsvpToCache = useCallback((attending: boolean) => {
    if (!parentEventId) return;
    queryClient.setQueryData(
      ['homebodyRxpEventDetails', parentEventId],
      (prev: any) => prev ? {
        ...prev,
        isAttending: attending,
        numberAttending: attending
          ? prev.numberAttending + (prev.isAttending ? 0 : 1)
          : Math.max(0, prev.numberAttending - (prev.isAttending ? 1 : 0)),
      } : prev
    );
  }, [parentEventId, queryClient]);

  // Stable refetch refs so the callbacks below aren't recreated every render
  // (React Query result objects change reference on each render).
  const { refetch: refetchParentEventDetails } = parentEventDetailsQuery;
  const { refetch: refetchRsvps } = rsvpsQuery;

  // After an RSVP write fails, re-fetch the source of truth and decide whether the
  // failure was because the event just filled up (race-loss) vs. a generic error.
  // Frontend-only: the backend returns a generic error, so we infer fullness here.
  const isEventNowFull = useCallback(async (): Promise<boolean> => {
    try {
      // Partner events: parent capacity is the source of truth
      if (parentEventId && !isBypassMode) {
        const { data } = await refetchParentEventDetails();
        // null capacity = unlimited; a partner event can never be "full"
        if (data?.totalCapacity == null) return false;
        return data.numberAttending >= data.totalCapacity;
      }
      // Regular events: sum RSVP attendees vs the event cap (null/0 = unlimited)
      const { data } = await refetchRsvps();
      const list = (data as RSVP[] | undefined) ?? [];
      const attending = list.reduce((sum, r) => sum + r.totalAttendees, 0);
      const max = gathering?.maxEventAttendees;
      return max != null && max > 0 && attending >= max;
    } catch {
      return false;
    }
  }, [parentEventId, isBypassMode, refetchParentEventDetails, refetchRsvps, gathering?.maxEventAttendees]);

  const handleUpdateRSVP = useCallback(async (totalAttendees: number) => {
    if (!rsvpObj || !gathering) return;
    try {
      setIsParentRsvpPending(true);
      if (parentEventId && !isBypassMode) await updateParentRsvp(parentEventId, true, eventFee);
      applyParentRsvpToCache(true);
      const cobuRsvp = user ? rsvps.find(r => r.user.uuid === user.uuid) : null;
      if (cobuRsvp) {
        await updateRSVPMutation.mutateAsync({ rsvpId: cobuRsvp.uuid, totalAttendees });
      } else {
        await createRSVPMutation.mutateAsync({ totalAttendees });
      }

      /* ANALYTICS_DISABLED: RSVPED_TO_GATHERING ('rsvped_to_gathering')
        analyticsService.trackEvent(AnalyticsEvent.RSVPED_TO_GATHERING, {
                user: user,
                gatheringId: gathering.uuid,
              });
        */

      await Promise.all([rsvpsQuery.refetch(), queryClient.invalidateQueries({ queryKey: ['gatherings'] })]);
      showSuccessMessage(strings.events.detail.labels.userRsvpConfirmation);
    } catch (err) {
      logger.error('Error updating RSVP:', err);
      showErrorMessage(
        (await isEventNowFull())
          ? strings.hooks.eventDetail.errors.eventFull
          : strings.hooks.eventDetail.errors.updateRsvpFailed
      );
      throw err;
    } finally {
      setIsParentRsvpPending(false);
    }
  }, [rsvpObj, gathering, parentEventId, isBypassMode, eventFee, user, rsvps, updateRSVPMutation, createRSVPMutation, applyParentRsvpToCache, rsvpsQuery, isEventNowFull, strings]);

  const handleCancelRSVP = useCallback(async () => {
    if (!rsvpObj || !gathering) return;
    try {
      setIsParentRsvpPending(true);
      if (parentEventId && !isBypassMode) await updateParentRsvp(parentEventId, false);
      applyParentRsvpToCache(false);
      const cobuRsvp = user ? rsvps.find(r => r.user.uuid === user.uuid) : null;
      if (cobuRsvp) {
        await deleteRSVPMutation.mutateAsync({ rsvpId: cobuRsvp.uuid });
      }

      /* ANALYTICS_DISABLED: DELETE_RSVP ('delete_rsvp')
        analyticsService.trackEvent(AnalyticsEvent.DELETE_RSVP, { eventId, userId: user?.uuid, gatheringId: gathering.uuid, rsvpId: rsvpObj.uuid });
        */

      await Promise.all([rsvpsQuery.refetch(), queryClient.invalidateQueries({ queryKey: ['gatherings'] })]);
      showSuccessMessage('RSVP cancelled');
    } catch (err) {
      logger.error('Error deleting RSVP:', err);
      showErrorMessage((err as Error)?.message || strings.hooks.eventDetail.errors.cancelRsvpFailed);
      throw err;
    } finally {
      setIsParentRsvpPending(false);
    }
  }, [rsvpObj, gathering, parentEventId, user, rsvps, deleteRSVPMutation, applyParentRsvpToCache, rsvpsQuery, strings]);

  // Comment handlers
  const handleCommentSubmit = useCallback(async () => {
    // Comments are handled by GroupDiscussion on web; no-op here
    return undefined as any;
  }, []);

  const handleDeleteComment = useCallback(async (_comment: Comment) => {
    // Handled by GroupDiscussion; no-op
  }, []);

  // Cancel gathering handler
  const handleCancelGathering = useCallback(async () => {
    if (!gathering) return;
    try {
      await cancelGatheringMutation.mutateAsync();
      navigate(-1);
    } catch (err) {
      logger.error('Error cancelling gathering:', err);
    }
  }, [gathering, cancelGatheringMutation, navigate]);

  // Modal handlers
  const handleWhosComingPress = useCallback(() => {
    setIsAllMembersModalVisible(true);
  }, []);

  const handleCloseAllMembersModal = useCallback(() => {
    setIsAllMembersModalVisible(false);
  }, []);

  // Report modal handlers
  const handleReportPress = useCallback(() => {
    setIsReportModalVisible(true);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setIsReportModalVisible(false);
  }, []);

  // Block user logic
  const blockUser = useCallback(async (_userToBlockUuid: string) => {
    // Not implemented on web yet
  }, []);

  const handleBlockUserPress = useCallback(async () => {
    // Not implemented on web yet
  }, []);

  // Refresh functionality
  const onRefresh = useCallback(async () => {
    const promises: Promise<any>[] = [gatheringQuery.refetch(), parentEventDetailsQuery.refetch()];

    if (eventDate) promises.push(rsvpsQuery.refetch());
    if (groupId) promises.push(groupMembersQuery.refetch());

    await Promise.all(promises);
  }, [gatheringQuery, parentEventDetailsQuery, rsvpsQuery, groupMembersQuery, eventDate, groupId]);

  const refreshData = {
    refreshing: gatheringQuery.isRefetching || parentEventDetailsQuery.isRefetching || rsvpsQuery.isRefetching || groupMembersQuery.isRefetching,
    onRefresh,
  };

  // RSVP handlers
  const handleRSVPPress = useCallback(async (totalAttendees: number = 1) => {
    if (!gathering || !user) return;
    try {
      setIsParentRsvpPending(true);
      if (parentEventId && !isBypassMode) await updateParentRsvp(parentEventId, true, eventFee);
      applyParentRsvpToCache(true);
      await createRSVPMutation.mutateAsync({ totalAttendees });
      await Promise.all([rsvpsQuery.refetch(), queryClient.invalidateQueries({ queryKey: ['gatherings'] })]);
      showSuccessMessage(strings.events.detail.labels.userRsvpConfirmation);
    } catch (err) {
      logger.error('Error creating RSVP:', err);
      showErrorMessage(
        (await isEventNowFull())
          ? strings.hooks.eventDetail.errors.eventFull
          : strings.hooks.eventDetail.errors.rsvpFailed
      );
      throw err;
    } finally {
      setIsParentRsvpPending(false);
    }
  }, [gathering, user, parentEventId, isBypassMode, eventFee, createRSVPMutation, applyParentRsvpToCache, rsvpsQuery, isEventNowFull, strings]);

  const handleComposeComment = useCallback((replyTo?: any) => {
    if (!gathering) return;
    const params = new URLSearchParams({
      groupEventId: gathering.uuid,
      commentType: 'GroupEventComment',
      eventDate,
    });
    if (replyTo) params.set('replyingTo', encodeURIComponent(JSON.stringify(replyTo)));
    navigate(`/events/compose-comment?${params.toString()}`);
  }, [navigate, gathering, eventDate]);

  return {
    // Data
    gathering,
    rsvps,
    currentMembers,
    currentUser: user || null,
    eventDate,

    // States
    isLoading,
    error,
    userIsGroupMember,
    isCurrentUserCreator,
    rsvpObj,
    isAllMembersModalVisible,
    isReportModalVisible,
    isRsvpActionPending: isParentRsvpPending,
    isParentDetailsLoading: parentEventDetailsQuery.isRefetching,

    // Computed values
    totalAttendance,
    rsvpTotalCapacity: (!isBypassMode && parentEventDetails) ? parentEventDetails.totalCapacity : undefined,

    // Handlers
    handleRSVPPress,
    handleUpdateRSVP,
    handleCancelRSVP,
    handleCommentSubmit,
    handleDeleteComment,
    handleCancelGathering,
    handleWhosComingPress,
    handleCloseAllMembersModal,
    handleReportPress,
    handleCloseReportModal,
    handleBlockUserPress,
    handleClose,
    handleRetry,
    refreshData,
    handleComposeComment,
  };
}; 